import { createFileRoute, Link } from "@tanstack/react-router";
import type { CardIdentifiable } from ":entities/card";
import type { CardReviewLog } from ":entities/card-review-log";
import { useReviewSession } from ":features/free-spaced-repetition-scheduling/lib/use-review-session";
import {
	ReviewSessionStart,
	ReviewFlashcardContainer,
	ReviewActions,
	ReviewComplete,
	type FlashcardSide,
} from ":features/free-spaced-repetition-scheduling/ui";
import { StoriesProgress } from ":features/free-spaced-repetition-scheduling/ui/stories-progress";
import { Icon } from ":shared/ui/icon";
import { buttonStyles } from "@simurgh/feathers/ui/button";
import { useCallback, useState, useTransition, Suspense } from "react";
import { FlashcardSkeleton } from ":shared/ui/skeleton";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
	"/$locale/dashboard_/decks/$deckId/review",
)({
	component: ReviewComponent,
});

function ReviewComponent() {
	const { t } = useTranslation();
	const [, startTransition] = useTransition();
	const { deckId, locale } = Route.useParams();

	const [side, setSide] = useState<FlashcardSide>("word");

	const {
		dueCards,
		submitReview,
		isPending,
		reviewIndex,
		totalCards,
		startSession,
	} = useReviewSession({
		deckId,
		onSubmitReview: () => {
			setSide("word");
		},
	});

	const handleRating = useCallback(
		async (card: CardIdentifiable, rating: CardReviewLog["rating"]) => {
			submitReview({
				card: card,
				rating: rating,
				duration: undefined,
			});
		},
		[submitReview],
	);

	const handleFlip = useCallback(() => {
		startTransition(() => {
			startTransition(() => {
				setSide((prev) => (prev === "word" ? "sense" : "word"));
			});
		});
	}, []);

	// Show start screen
	if (reviewIndex === -1) {
		return (
			<>
				<title>
					{t("pages.review.startTitle")} - {t("app.name")}
				</title>
				<meta name="description" content={t("pages.review.startTitle")} />
				<ReviewSessionStart
					totalCards={totalCards}
					deckId={deckId}
					onStartSession={startSession}
				/>
			</>
		);
	}

	// Show completion screen
	if (reviewIndex >= dueCards.length) {
		return (
			<>
				<title>
					{t("pages.review.completeTitle")} - {t("app.name")}
				</title>
				<meta name="description" content={t("pages.review.completeTitle")} />
				<ReviewComplete reviewIndex={reviewIndex} deckId={deckId} />
			</>
		);
	}

	// Show active review session
	return (
		<>
			<title>
				{t("pages.review.title")} - {t("app.name")}
			</title>
			<meta name="description" content={t("pages.review.ariaLabel")} />
			<div
				className="flex flex-col justify-between outline-none gap-4 p-2 lg:p-6 min-h-screen h-full"
				tabIndex={0}
				aria-label={t("pages.review.ariaLabel")}
			>
				<div className="flex items-center" dir="auto">
					<Link
						className={buttonStyles({ size: "sq-md", intent: "plain" })}
						to="/$locale/dashboard/decks/$deckId"
						params={{ deckId, locale }}
					>
						<Icon name="hugeicons-arrow-left-02" size={24} />
					</Link>
					<StoriesProgress
						currentSegment={reviewIndex}
						totalSegments={totalCards}
					/>
				</div>

				<Suspense fallback={<FlashcardSkeleton />}>
					{dueCards.map((item, index) => (
						<ReviewFlashcardContainer
							isActive={index === reviewIndex}
							key={item.card.id}
							className="grow"
							currentCard={item}
							side={side}
							onFlip={handleFlip}
							onNextSense={() => {}}
						/>
					))}
				</Suspense>

				<ReviewActions
					side={side}
					isPending={isPending}
					onFlip={handleFlip}
					onRating={handleRating}
					currentCard={dueCards[reviewIndex]}
				/>
			</div>
		</>
	);
}
